import React from 'react';
import {
  Card,
  Typography,
  Tag,
  Space,
  Badge,
  Flex,
  Timeline,
  Divider,
  Alert,
  List,
  Modal,
  Input,
  Button as AntButton,
  Row,
  Col,
  Descriptions,
  Dropdown,
  Select,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  CloudOutlined,
  DesktopOutlined,
  UserOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ToolOutlined,
  ExperimentOutlined,
  FieldTimeOutlined,
  ApiOutlined,
  CopyOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Button } from '~/app/components/common/Button';
import { AssetLogJsonPayload } from '~/app/components/asset-detail/AssetLogJsonPayload';
import { copyAssetLogPayload } from '~/app/components/asset-detail/assetLogContent.utils';
import logJsonStyles from '~/app/components/asset-detail/AssetLogsTable/AssetLogsTable.module.css';
import { Loader } from '~/app/components/common/Loader/Loader';
import { IncidentDetailLinkedContext } from '~/app/components/incident/IncidentDetailLinkedContext/IncidentDetailLinkedContext';
import {
  type ConcernedLog,
  type ConnectorSnapshot,
  type IncidentDetail,
  type TimelineEntry,
  incidentStatusConfig,
} from '~/app/dashboard/mock-data';
import { getDiagnosisSchedule } from '~/app/dashboard/components/incidentDiagnosisSchedule.utils';
import { ruleUsedLabel } from '~/app/dashboard/components/incidentRulesUsed.utils';
import { INCIDENT_BLOCKING_REASONS } from '@/app/hooks/incident/useIncidentStatusTransition';
import type { BlockingReason, IncidentStatus } from '@evimo/types';
import { formatLogIdDisplay } from '~/app/utils/format-log-id';
import { incidentDisplayCode } from '~/app/utils/incident-display-code';
import { formatIncidentOrigins } from '~/app/utils/incident-origins';

const { Title, Text, Paragraph } = Typography;

const sourceIcon: Record<string, React.ReactNode> = {
  external: <CloudOutlined style={{ fontSize: 14 }} />,
  'machine-log': <DesktopOutlined style={{ fontSize: 14 }} />,
  'client-complaint': <UserOutlined style={{ fontSize: 14 }} />,
};

const sourceColor: Record<string, string> = {
  external: '#1E6DF6',
  'machine-log': '#ec7e00',
  'client-complaint': '#722ed1',
};

const connectorStatusConfig: Record<
  ConnectorSnapshot['status'],
  { color: string; translationKey: string }
> = {
  available: { color: 'green', translationKey: 'detail.connectors.available' },
  derated: { color: 'gold', translationKey: 'detail.connectors.derated' },
  faulted: { color: 'red', translationKey: 'detail.connectors.faulted' },
  offline: { color: 'default', translationKey: 'detail.connectors.offline' },
};

function renderPill(label: string, color: string) {
  return (
    <Tag
      bordered={false}
      style={{
        marginInlineEnd: 0,
        borderRadius: 9999,
        paddingInline: 10,
        paddingBlock: 4,
        fontWeight: 600,
        fontSize: 12,
        lineHeight: 1.2,
        color: '#191c1f',
        background: `${color}1A`,
        border: `1px solid ${color}33`,
      }}
    >
      {label}
    </Tag>
  );
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris',
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris',
  });
}

function stripTierPrefix(value: string): string {
  return value
    .replace(/^\[TIER_\d+\]\s*(?:\/|\|)\s*/, '')
    .replace(/^(?:Secondary Signal|Heartbeat Warning):\s*/i, '')
    .trim();
}

function formatRequiredPartLabel(
  partNumber: string,
  description: string,
): string {
  const normalizedPartNumber = partNumber.trim();

  if (normalizedPartNumber && normalizedPartNumber !== 'N/A') {
    return normalizedPartNumber;
  }

  return description;
}

const detailPanelHeight = 560;
const visibleConcernedLogsCount = 5;

type IncidentDetailViewProps = {
  assignModalOpen: boolean;
  assignee: string;
  detail: IncidentDetail | null;
  errorMessage: string | null;
  blockingReason: BlockingReason;
  confirmBlockedTransition: () => void;
  cancelPendingTransition: () => void;
  hasPersistedDiagnosis: boolean;
  isDiagnosing: boolean;
  isLaunchingOnsiteIntervention: boolean;
  isLaunchingRemoteAction: boolean;
  isLoading: boolean;
  isTransitionDialogOpen: boolean;
  isTransitioning: boolean;
  onsiteMagicLink: string | null;
  onsiteRecommendedActions: string[];
  pendingTransition: { incidentId: string; nextStatus: IncidentStatus } | null;
  showAssignAction: boolean;
  onAssigneeChangeAction: (nextValue: string) => void;
  onBackAction: () => void;
  onCloseAssignModalAction: () => void;
  onCloseOnsiteModalAction: () => void;
  onConfirmAssignAction: () => void;
  onCopyOnsiteMagicLinkAction: () => void;
  onDiagnoseAction: () => void;
  onLaunchOnsiteInterventionAction: () => void;
  onOpenAssignModalAction: () => void;
  onTriggerRemoteActionAction: () => void;
  onStatusTransitionAction: (
    incidentId: string,
    nextStatus: IncidentStatus,
  ) => void;
  onBlockingReasonChangeAction: (nextValue: BlockingReason) => void;
  transitioningIncidentId: string | null;
};

export function IncidentDetailView({
  assignModalOpen,
  assignee,
  blockingReason,
  confirmBlockedTransition,
  cancelPendingTransition,
  detail,
  errorMessage,
  hasPersistedDiagnosis,
  isDiagnosing,
  isLaunchingOnsiteIntervention,
  isLaunchingRemoteAction,
  isLoading,
  isTransitionDialogOpen,
  isTransitioning,
  onsiteMagicLink,
  onsiteRecommendedActions,
  pendingTransition,
  showAssignAction,
  onAssigneeChangeAction,
  onBackAction,
  onCloseAssignModalAction,
  onCloseOnsiteModalAction,
  onConfirmAssignAction,
  onCopyOnsiteMagicLinkAction,
  onDiagnoseAction,
  onLaunchOnsiteInterventionAction,
  onOpenAssignModalAction,
  onTriggerRemoteActionAction,
  onStatusTransitionAction,
  onBlockingReasonChangeAction,
  transitioningIncidentId,
}: IncidentDetailViewProps) {
  const [isConcernedLogsModalOpen, setIsConcernedLogsModalOpen] =
    React.useState(false);
  const [selectedConcernedLog, setSelectedConcernedLog] =
    React.useState<ConcernedLog | null>(null);
  const { t } = useTranslation('incidents');

  if (isLoading) {
    return (
      <div
        className="app-page"
        style={{
          maxWidth: 1360,
          width: '100%',
          margin: '0 auto',
          minHeight: 360,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Loader size="large" />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div
        className="app-page"
        style={{ maxWidth: 1360, width: '100%', margin: '0 auto' }}
      >
        <Space
          style={{ cursor: 'pointer', marginBottom: 8 }}
          onClick={onBackAction}
        >
          <ArrowLeftOutlined />
          <Text>{t('detail.back', 'Back')}</Text>
        </Space>
        <Alert
          type="error"
          showIcon
          message={t('detail.loadFailed', 'Failed to load incident details')}
          description={errorMessage}
        />
      </div>
    );
  }

  if (!detail) {
    return (
      <div
        className="app-page"
        style={{ maxWidth: 1360, width: '100%', margin: '0 auto' }}
      >
        <Space
          style={{ cursor: 'pointer', marginBottom: 8 }}
          onClick={onBackAction}
        >
          <ArrowLeftOutlined />
          <Text>{t('detail.back', 'Back')}</Text>
        </Space>

        <Alert
          type="error"
          showIcon
          message={t('detail.notFound', 'Incident detail not found')}
          description={t(
            'detail.notFoundDescription',
            'No incident detail is available for this incident ID.',
          )}
        />
      </div>
    );
  }

  const { incident, timeline, diagnostics, connectors, concernedLogs } = detail;
  const visibleConcernedLogs = concernedLogs.slice(
    0,
    visibleConcernedLogsCount,
  );
  const armExecutionCommands = diagnostics.armExecutionCommands ?? [];
  const recommendedRemoteActions = diagnostics.recommendedRemoteActions ?? [];
  const diagnosisSchedule = getDiagnosisSchedule(
    diagnostics,
    hasPersistedDiagnosis,
  );
  const hasHiddenConcernedLogs =
    concernedLogs.length > visibleConcernedLogs.length;
  const sev = {
    color:
      incident.severity === 'critical'
        ? '#e23b4a'
        : incident.severity === 'high'
          ? '#ec7e00'
          : incident.severity === 'medium'
            ? '#faad14'
            : '#00a87e',
    label: t(`severity.${incident.severity}`, incident.severity),
  };
  const incidentStatus = incidentStatusConfig[incident.status] ?? {
    color: '#8d969e',
  };
  const actionItems = [
    ...(showAssignAction
      ? [
          {
            key: 'assign',
            label: t('detail.actions.assign', 'Assign'),
            icon: <PlusOutlined />,
            onClick: onOpenAssignModalAction,
          },
        ]
      : []),
    ...((incident.availableTransitions ?? []).map((nextStatus) => ({
      key: `transition-${nextStatus}`,
      label: t('actions.transitionTo', {
        status: t(`status.${nextStatus}`, nextStatus),
        defaultValue: 'Move to {{status}}',
      }),
      onClick: () => onStatusTransitionAction(incident.id, nextStatus),
    })) ?? []),
  ];

  const handleCopyConcernedLog = async (rawLog: unknown) => {
    const ok = await copyAssetLogPayload(rawLog);
    if (ok) {
      message.success(t('detail.logs.copySuccess', 'Payload copied'));
      return;
    }
    message.error(t('detail.logs.copyError', 'Failed to copy payload'));
  };

  const renderConcernedLogPayload = (
    rawLog: unknown,
    maxHeight: number,
  ): React.ReactNode => (
    <section className={logJsonStyles.payloadCard}>
      <div className={logJsonStyles.payloadHeader}>
        <h4 className={logJsonStyles.payloadTitle}>
          {t('detail.logs.payloadTitle', 'Payload (JSON)')}
        </h4>
        <AntButton
          type="default"
          size="small"
          icon={<CopyOutlined />}
          className={logJsonStyles.payloadCopyButton}
          onClick={(event) => {
            event.stopPropagation();
            void handleCopyConcernedLog(rawLog);
          }}
        >
          {t('detail.logs.copy', 'Copy')}
        </AntButton>
      </div>
      <div className={logJsonStyles.payloadBody}>
        <AssetLogJsonPayload value={rawLog} maxHeight={maxHeight} />
      </div>
    </section>
  );

  const renderConcernedLogCard = (
    log: ConcernedLog,
    options?: {
      compact?: boolean;
    },
  ) => {
    const compact = options?.compact ?? false;

    return (
      <Card
        key={log.logId}
        size="small"
        hoverable={compact}
        onClick={
          compact
            ? () => {
                setSelectedConcernedLog(log);
              }
            : undefined
        }
        style={compact ? { cursor: 'pointer' } : undefined}
        title={
          <Text
            copyable={{ text: log.logId }}
            strong
            style={{ fontFamily: 'monospace', fontSize: 13 }}
            onClick={(event) => event.stopPropagation()}
          >
            {formatLogIdDisplay(log.logId)}
          </Text>
        }
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Text type="secondary">
              {t('detail.logs.receivedAt', 'Received')}:{' '}
              {log.receivedAt ? formatDateTime(log.receivedAt) : '-'}
            </Text>
            <Text type="secondary">
              {t('detail.logs.createdAt', 'Stored')}:{' '}
              {formatDateTime(log.createdAt)}
            </Text>
          </Space>

          {renderConcernedLogPayload(log.rawLog, compact ? 180 : 480)}
        </Space>
      </Card>
    );
  };

  return (
    <div
      className="app-page"
      style={{ maxWidth: 1360, width: '100%', margin: '0 auto' }}
    >
      <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
        <Space style={{ cursor: 'pointer' }} onClick={onBackAction}>
          <ArrowLeftOutlined />
          <Text>{t('detail.back', 'Back')}</Text>
        </Space>

        {actionItems.length > 0 && (
          <Dropdown
            trigger={['click']}
            menu={{
              items: actionItems,
            }}
          >
            <AntButton
              aria-label={t('detail.actions.label', 'Actions')}
              icon={<MoreOutlined />}
              variant="outlined"
              loading={
                isTransitioning && transitioningIncidentId === incident.id
              }
            />
          </Dropdown>
        )}
      </Flex>

      <Card style={{ marginBottom: 8 }}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Flex justify="space-between" align="flex-start" wrap="wrap" gap={12}>
            <div>
              <Space size={12} wrap>
                <Title level={3} style={{ margin: 0 }}>
                  {incidentDisplayCode(incident)}
                </Title>
                {renderPill(sev.label, sev.color)}
                {renderPill(
                  t(`status.${incident.status}`, incident.status),
                  incidentStatus.color,
                )}
              </Space>
              {incident.title &&
              incident.title !== incidentDisplayCode(incident) ? (
                <Title
                  level={5}
                  style={{ margin: '8px 0 4px', fontWeight: 500 }}
                >
                  {incident.title}
                </Title>
              ) : null}
            </div>
          </Flex>

          <IncidentDetailLinkedContext
            stationId={incident.stationId}
            stationName={incident.stationName}
            location={incident.location}
            primaryAsset={incident.primaryAsset}
          />

          <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 4 }}>
            <Descriptions.Item label={t('detail.summary.origin', 'Origin')}>
              {formatIncidentOrigins(incident.origin) || '—'}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('detail.summary.affectedBays', 'Affected Bays')}
            >
              {incident.affectedChargers} / {incident.totalChargers}
            </Descriptions.Item>
            {/* <Descriptions.Item label={t('detail.summary.started', 'Started')}>
              {formatDateTime(incident.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label={t('detail.summary.updated', 'Updated')}>
              {formatDateTime(incident.updatedAt)}
            </Descriptions.Item> */}
          </Descriptions>
        </Space>
      </Card>

      <Card
        title={t('detail.logs.title', 'Concerned Logs')}
        style={{ marginBottom: 16 }}
        extra={
          <Space size={12}>
            {renderPill(String(concernedLogs.length), '#1E6DF6')}
            {hasHiddenConcernedLogs ? (
              <AntButton
                type="link"
                style={{ paddingInline: 0 }}
                onClick={() => setIsConcernedLogsModalOpen(true)}
              >
                {t('detail.logs.showAll', 'Show all')}
              </AntButton>
            ) : null}
          </Space>
        }
      >
        {concernedLogs.length === 0 ? (
          <Text type="secondary">
            {t(
              'detail.logs.empty',
              'No logs were linked to the incident events.',
            )}
          </Text>
        ) : (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Row gutter={[12, 12]}>
              {visibleConcernedLogs.map((log) => (
                <Col key={log.logId} xs={24} md={12} xl={8}>
                  {renderConcernedLogCard(log, { compact: true })}
                </Col>
              ))}
            </Row>

            {hasHiddenConcernedLogs ? (
              <Flex justify="center">
                <AntButton
                  type="link"
                  onClick={() => setIsConcernedLogsModalOpen(true)}
                >
                  {t('detail.logs.showAllCount', 'Show all {{count}} logs', {
                    count: concernedLogs.length,
                  })}
                </AntButton>
              </Flex>
            ) : null}
          </Space>
        )}
      </Card>

      <Row gutter={[16, 16]} style={{ marginTop: 8, alignItems: 'stretch' }}>
        <Col xs={24} lg={9} style={{ display: 'flex' }}>
          <Card
            title={
              <Space>
                <ApiOutlined />
                <span>{t('detail.connectors.title', 'Connector Status')}</span>
              </Space>
            }
            style={{ width: '100%', height: '100%' }}
            styles={{
              body: {
                height: detailPanelHeight,
                overflowY: 'auto',
              },
            }}
          >
            <List
              itemLayout="vertical"
              dataSource={connectors}
              renderItem={(connector) => {
                const status = connectorStatusConfig[connector.status];

                return (
                  <List.Item
                    key={connector.code}
                    extra={renderPill(
                      t(status.translationKey, connector.status),
                      status.color === 'green'
                        ? '#00a87e'
                        : status.color === 'gold'
                          ? '#faad14'
                          : status.color === 'red'
                            ? '#e23b4a'
                            : '#8d969e',
                    )}
                  >
                    <List.Item.Meta
                      title={<Text strong>{connector.name}</Text>}
                      description={
                        <Space wrap size={[8, 8]}>
                          <Text code>{connector.code}</Text>
                          <Tag>{connector.connectorType}</Tag>
                          <Text type="secondary">{connector.powerKw} kW</Text>
                        </Space>
                      }
                    />

                    <Text
                      type="secondary"
                      style={{ display: 'block', marginTop: 4 }}
                    >
                      {connector.note}
                    </Text>
                  </List.Item>
                );
              }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={15} style={{ display: 'flex' }}>
          <Card
            title={
              <Space>
                <FieldTimeOutlined />
                <span>
                  {t(
                    'detail.timeline.title',
                    'Event Timeline - AI-Fused Data Sources',
                  )}
                </span>
              </Space>
            }
            style={{ width: '100%', height: '100%' }}
            styles={{
              body: {
                height: detailPanelHeight,
                overflowY: 'auto',
              },
            }}
          >
            <Timeline
              items={timeline.map((entry: TimelineEntry) => {
                const showStateLine =
                  entry.normalizedState &&
                  (entry.rawCode || entry.normalizedState !== 'AVAILABLE');

                return {
                  color: sourceColor[entry.source] ?? '#999',
                  dot: sourceIcon[entry.source],
                  children: (
                    <Space
                      direction="vertical"
                      size={8}
                      style={{ paddingBottom: 16 }}
                    >
                      <Text
                        strong
                        style={{ fontFamily: 'monospace', fontSize: 13 }}
                      >
                        {formatDateTime(entry.time)}
                      </Text>
                      <Flex gap={8} align="center" style={{ marginBottom: 4 }}>
                        <Tag
                          color={
                            entry.source === 'external'
                              ? 'blue'
                              : entry.source === 'machine-log'
                                ? 'orange'
                                : 'purple'
                          }
                        >
                          {entry.sourceLabel}
                        </Tag>
                      </Flex>
                      <Text
                        strong
                        style={{ display: 'block', marginBottom: 4 }}
                      >
                        {stripTierPrefix(entry.title)}
                      </Text>
                      <Paragraph
                        type="secondary"
                        style={{ margin: 0, fontSize: 13, lineHeight: '1.5' }}
                      >
                        {stripTierPrefix(entry.description)}
                      </Paragraph>

                      {(entry.rawCode || showStateLine) && (
                        <Descriptions
                          size="small"
                          bordered
                          column={1}
                          styles={{
                            root: {
                              marginTop: 4,
                              background: '#fafafa',
                            },
                            label: {
                              width: 124,
                              fontSize: 12,
                            },
                            content: {
                              fontSize: 12,
                            },
                          }}
                          items={[
                            ...(entry.rawCode
                              ? [
                                  {
                                    key: 'code',
                                    label:
                                      entry.source === 'machine-log'
                                        ? t(
                                            'detail.timeline.anomalyRule',
                                            'Anomaly rule',
                                          )
                                        : t(
                                            'detail.timeline.manufacturerCode',
                                            'Manufacturer Code',
                                          ),
                                    children: <Text code>{entry.rawCode}</Text>,
                                  },
                                ]
                              : []),
                            ...(entry.translatedCode
                              ? [
                                  {
                                    key: 'meaning',
                                    label: t(
                                      'detail.timeline.codeMeaning',
                                      'Meaning',
                                    ),
                                    children: (
                                      <Text strong>{entry.translatedCode}</Text>
                                    ),
                                  },
                                ]
                              : []),
                            ...(showStateLine
                              ? [
                                  {
                                    key: 'state',
                                    label: t(
                                      'detail.timeline.normalizedState',
                                      'Normalized State',
                                    ),
                                    children: (
                                      <Text>{entry.normalizedState}</Text>
                                    ),
                                  },
                                ]
                              : []),
                          ]}
                        />
                      )}
                      {entry.stateConflict && (
                        <Tag color="warning">
                          {t(
                            'detail.timeline.stateCodeConflict',
                            'Reported as healthy despite an active error code',
                          )}
                        </Tag>
                      )}
                    </Space>
                  ),
                };
              })}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <Space>
            <ExperimentOutlined />
            <span>
              {t('detail.diagnostics.title', 'Diagnostic Assessment')}
            </span>
          </Space>
        }
        extra={
          <Space size={12} wrap>
            {diagnosisSchedule ? (
              <Text type="secondary" data-testid="diagnosis-scheduled-at">
                {diagnosisSchedule.overdue
                  ? t('detail.diagnostics.pending', 'Diagnosis pending')
                  : t('detail.diagnostics.scheduledAt', {
                      time: formatDateTime(
                        diagnosisSchedule.dueAt.toISOString(),
                      ),
                      defaultValue: 'Diagnosis scheduled at {{time}}',
                    })}
              </Text>
            ) : null}
            <Button
              type="primary"
              icon={<ExperimentOutlined />}
              loading={isDiagnosing}
              loadingText={t(
                'detail.diagnostics.running',
                'Analyzing telemetry and logs...',
              )}
              onClick={onDiagnoseAction}
              disabled={hasPersistedDiagnosis}
            >
              {hasPersistedDiagnosis
                ? t(
                    'detail.diagnostics.diagnosisAvailable',
                    'Diagnosis already available',
                  )
                : t('detail.diagnostics.runDiagnosis', 'Run diagnosis')}
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div className="app-diagnostic-panel">
            <Text className="app-diagnostic-panel-label">
              {t('detail.diagnostics.incidentSummary', 'Incident Summary')}
            </Text>
            <Paragraph className="app-diagnostic-panel-body">
              {diagnostics.incidentSummary}
            </Paragraph>
          </div>

          <div className="app-diagnostic-panel">
            <Text className="app-diagnostic-panel-label">
              {t('detail.diagnostics.rootCauseAnalysis', 'Root Cause Analysis')}
            </Text>
            <Paragraph className="app-diagnostic-panel-body">
              {diagnostics.rootCauseAnalysis}
            </Paragraph>
          </div>
          {diagnostics.rulesUsed?.length ? (
            <div
              className="app-diagnostic-panel"
              data-testid="diagnosis-rules-used"
            >
              <Text className="app-diagnostic-panel-label">
                {t('detail.diagnostics.rulesUsed', 'Rules used')}
              </Text>
              <Space size={[4, 4]} wrap>
                {diagnostics.rulesUsed.map((rule) => (
                  <Tag
                    key={rule.ruleId}
                    color={rule.source === 'GLOBAL' ? 'blue' : 'purple'}
                  >
                    {ruleUsedLabel(
                      rule,
                      t('detail.diagnostics.evimoRule', 'EVIMO rule'),
                    )}
                  </Tag>
                ))}
              </Space>
            </div>
          ) : null}
          {diagnostics.rulesUnavailable ? (
            <Alert
              type="warning"
              showIcon
              data-testid="diagnosis-rules-unavailable"
              message={t(
                'detail.diagnostics.rulesUnavailable',
                'The diagnostic rules could not be searched: this diagnosis ran without them. Diagnose again later.',
              )}
            />
          ) : null}

          <Divider style={{ margin: 0 }} />

          <List
            header={
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Text strong>
                  {t(
                    'detail.diagnostics.recommendedRemoteActions',
                    'Recommended Remote Actions',
                  )}
                </Text>
              </Space>
            }
            locale={{
              emptyText: t(
                'detail.diagnostics.noRemoteActions',
                'No remote actions were recommended by the latest diagnosis.',
              ),
            }}
            size="small"
            dataSource={recommendedRemoteActions}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button
                    key={`remote-${item.id}`}
                    type="default"
                    loading={isLaunchingRemoteAction}
                    onClick={onTriggerRemoteActionAction}
                  >
                    {t(
                      'detail.diagnostics.launchRemoteAction',
                      'Launch Remote Action',
                    )}
                  </Button>,
                ]}
              >
                <Space>
                  <Badge count="R" color="#1E6DF6" />
                  <Text>{item.description}</Text>
                </Space>
              </List.Item>
            )}
          />

          {armExecutionCommands.length > 0 ? (
            <>
              <Divider style={{ margin: 0 }} />

              <List
                header={
                  <Text strong>
                    {t(
                      'detail.diagnostics.armExecutionLog',
                      'ARM Execution Log',
                    )}
                  </Text>
                }
                size="small"
                dataSource={armExecutionCommands}
                renderItem={(item) => (
                  <List.Item>
                    <Flex
                      justify="space-between"
                      align="center"
                      style={{ width: '100%' }}
                    >
                      <Space direction="vertical" size={2}>
                        <Space>
                          {item.status === 'FAILED' ? (
                            <CloseCircleOutlined style={{ color: '#e23b4a' }} />
                          ) : item.status === 'SUCCESS' ? (
                            <CheckCircleOutlined style={{ color: '#00a87e' }} />
                          ) : (
                            <FieldTimeOutlined style={{ color: '#1E6DF6' }} />
                          )}
                          <Text strong>
                            {item.operation ?? item.actionId ?? item.commandId}
                          </Text>
                        </Space>
                        <Space size={8} wrap>
                          <Text type="secondary">{item.commandId}</Text>
                          {item.command ? (
                            <Text code style={{ fontSize: 11 }}>
                              {item.command}
                            </Text>
                          ) : null}
                          {item.targetAssetId ? (
                            <Text type="secondary">
                              {t('detail.diagnostics.assetLabel', 'Asset')}:{' '}
                              {item.targetAssetId}
                            </Text>
                          ) : null}
                          {item.protocol ? (
                            <Text type="secondary">
                              {t(
                                'detail.diagnostics.protocolLabel',
                                'Protocol',
                              )}
                              : {item.protocol}
                            </Text>
                          ) : null}
                        </Space>
                      </Space>
                      <Space>
                        {item.createdAt ? (
                          <Text code>{formatDateTime(item.createdAt)}</Text>
                        ) : null}
                        {renderPill(
                          item.status,
                          item.status === 'FAILED'
                            ? '#e23b4a'
                            : item.status === 'SUCCESS'
                              ? '#00a87e'
                              : '#1E6DF6',
                        )}
                      </Space>
                    </Flex>
                  </List.Item>
                )}
              />
            </>
          ) : null}

          <Divider style={{ margin: 0 }} />

          <List
            header={
              <Text strong>
                {t(
                  'detail.diagnostics.attemptedRemoteActions',
                  'Attempted Remote Actions',
                )}
              </Text>
            }
            size="small"
            dataSource={diagnostics.remoteActions}
            renderItem={(item) => (
              <List.Item>
                <Flex
                  justify="space-between"
                  align="center"
                  style={{ width: '100%' }}
                >
                  <Space>
                    {item.status === 'FAILED' ? (
                      <CloseCircleOutlined style={{ color: '#e23b4a' }} />
                    ) : item.status === 'PENDING' ? (
                      <FieldTimeOutlined style={{ color: '#1E6DF6' }} />
                    ) : (
                      <CheckCircleOutlined style={{ color: '#00a87e' }} />
                    )}
                    <Text>{item.action}</Text>
                  </Space>
                  <Space>
                    <Text code>{formatTime(item.timestamp)}</Text>
                    {renderPill(
                      item.status,
                      item.status === 'FAILED'
                        ? '#e23b4a'
                        : item.status === 'PENDING'
                          ? '#1E6DF6'
                          : '#00a87e',
                    )}
                  </Space>
                </Flex>
              </List.Item>
            )}
          />

          <Divider style={{ margin: 0 }} />

          <List
            header={
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <ToolOutlined />
                <Text strong>
                  {t(
                    'detail.diagnostics.recommendedOnSiteActions',
                    'Recommended On-Site Actions',
                  )}
                </Text>
                {diagnostics.recommendedOnSiteActions.length > 0 ? (
                  <Button
                    type="default"
                    loading={isLaunchingOnsiteIntervention}
                    onClick={onLaunchOnsiteInterventionAction}
                  >
                    {t(
                      'detail.diagnostics.launchOnsiteIntervention',
                      'Launch Onsite Intervention',
                    )}
                  </Button>
                ) : null}
              </Space>
            }
            locale={{
              emptyText: t(
                'detail.diagnostics.noOnsiteActions',
                'No onsite actions were recommended by the latest diagnosis.',
              ),
            }}
            size="small"
            dataSource={diagnostics.recommendedOnSiteActions}
            renderItem={(action, index) => (
              <List.Item>
                <Space>
                  <Badge count={index + 1} />
                  <Text>{action}</Text>
                </Space>
              </List.Item>
            )}
          />

          <Descriptions size="small" column={{ xs: 1, md: 2 }}>
            <Descriptions.Item
              label={t('detail.diagnostics.partsRequired', 'Parts Required')}
            >
              {diagnostics.partsRequired.length ? (
                <Space wrap>
                  {diagnostics.partsRequired.map((part) => (
                    <Tag
                      key={`${part.partNumber}-${part.description}`}
                      title={part.description}
                    >
                      {formatRequiredPartLabel(
                        part.partNumber,
                        part.description,
                      )}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Text type="secondary">
                  {t(
                    'detail.diagnostics.partsRequiredUnavailable',
                    'Not available',
                  )}
                </Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={t(
                'detail.diagnostics.estimatedResolution',
                'Estimated Resolution',
              )}
            >
              {renderPill(diagnostics.estimatedResolutionTime, '#8d969e')}
            </Descriptions.Item>
          </Descriptions>
        </Space>
      </Card>

      <Modal
        open={Boolean(onsiteMagicLink)}
        title={t(
          'detail.successModal.title',
          'Work Order Created & Technician Dispatched',
        )}
        onCancel={onCloseOnsiteModalAction}
        footer={null}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <List
            size="small"
            header={
              <Text strong>
                {t(
                  'detail.diagnostics.recommendedOnSiteActions',
                  'Recommended On-Site Actions',
                )}
              </Text>
            }
            dataSource={onsiteRecommendedActions}
            renderItem={(action, index) => (
              <List.Item>
                <Space>
                  <Badge count={index + 1} />
                  <Text>{action}</Text>
                </Space>
              </List.Item>
            )}
          />
          <Input.Search
            readOnly
            value={onsiteMagicLink ?? ''}
            enterButton={
              <AntButton
                icon={<CopyOutlined />}
                type="primary"
                onClick={onCopyOnsiteMagicLinkAction}
              >
                {t('detail.successModal.copyToClipboard', 'Copy to Clipboard')}
              </AntButton>
            }
          />
        </Space>
      </Modal>

      <Modal
        open={isTransitionDialogOpen}
        title={t('actions.blockedModal.title', 'Set blocking reason')}
        onCancel={cancelPendingTransition}
        footer={[
          <AntButton
            key="cancel"
            onClick={cancelPendingTransition}
            disabled={isTransitioning}
          >
            {t('actions.cancel', 'Cancel')}
          </AntButton>,
          <AntButton
            key="confirm"
            type="primary"
            loading={isTransitioning}
            onClick={() => {
              void confirmBlockedTransition();
            }}
          >
            {t('actions.confirm', 'Confirm')}
          </AntButton>,
        ]}
        centered
        width={480}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text>
            {t(
              'actions.blockedModal.description',
              'Choose the reason that blocks this incident before moving it to Blocked.',
            )}
          </Text>
          <Select<BlockingReason>
            value={blockingReason}
            options={INCIDENT_BLOCKING_REASONS.map((reason) => ({
              label: t(`blockingReason.${reason}`, reason),
              value: reason,
            }))}
            onChange={onBlockingReasonChangeAction}
          />
          {pendingTransition ? (
            <Text type="secondary">
              {t('actions.blockedModal.targetStatus', {
                status: t(
                  `status.${pendingTransition.nextStatus}`,
                  pendingTransition.nextStatus,
                ),
                defaultValue: 'Next status: {{status}}',
              })}
            </Text>
          ) : null}
        </Space>
      </Modal>

      <Modal
        open={assignModalOpen}
        title={t('detail.assignModal.title', 'Assign Incident')}
        onCancel={onCloseAssignModalAction}
        footer={[
          <AntButton key="cancel" onClick={onCloseAssignModalAction}>
            {t('detail.assignModal.cancel', 'Cancel')}
          </AntButton>,
          <AntButton
            key="confirm"
            type="primary"
            disabled={!assignee.trim()}
            onClick={onConfirmAssignAction}
          >
            {t('detail.assignModal.confirm', 'Confirm')}
          </AntButton>,
        ]}
        centered
        width={460}
      >
        <Space
          direction="vertical"
          size={12}
          style={{ width: '100%', padding: '8px 0' }}
        >
          <Text>
            {t(
              'detail.assignModal.description',
              'Assign this incident to an operator for review and action.',
            )}
          </Text>
          <Input
            placeholder={t(
              'detail.assignModal.placeholder',
              'Operator name or email',
            )}
            value={assignee}
            onChange={(e) => onAssigneeChangeAction(e.target.value)}
            onPressEnter={() => {
              if (assignee.trim()) {
                onConfirmAssignAction();
              }
            }}
          />
        </Space>
      </Modal>

      <Modal
        open={isConcernedLogsModalOpen}
        title={t('detail.logs.modalTitle', 'Concerned Logs')}
        onCancel={() => setIsConcernedLogsModalOpen(false)}
        footer={null}
        width={920}
        centered
        destroyOnHidden
        styles={{
          header: { paddingInline: 32, paddingTop: 16, paddingBottom: 0 },
          body: {
            height: '70vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingInline: 32,
            paddingBlock: 16,
          },
        }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text type="secondary">
            {t(
              'detail.logs.modalDescription',
              'All logs linked to this incident are available below.',
            )}
          </Text>

          <Row gutter={[12, 12]}>
            {concernedLogs.map((log) => (
              <Col key={log.logId} xs={24}>
                {renderConcernedLogCard(log)}
              </Col>
            ))}
          </Row>
        </Space>
      </Modal>

      <Modal
        open={selectedConcernedLog != null}
        title={t('detail.logs.logModalTitle', {
          logId: selectedConcernedLog
            ? formatLogIdDisplay(selectedConcernedLog.logId)
            : '',
          defaultValue: 'Log {{logId}}',
        })}
        onCancel={() => setSelectedConcernedLog(null)}
        footer={null}
        width={720}
        centered
        destroyOnHidden
        styles={{
          header: { paddingInline: 32, paddingTop: 16, paddingBottom: 0 },
          body: {
            height: '70vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingInline: 32,
            paddingBlock: 16,
          },
        }}
      >
        {selectedConcernedLog ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text
                copyable={{ text: selectedConcernedLog.logId }}
                strong
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              >
                {formatLogIdDisplay(selectedConcernedLog.logId)}
              </Text>
              <Text type="secondary">
                {t('detail.logs.receivedAt', 'Received')}:{' '}
                {selectedConcernedLog.receivedAt
                  ? formatDateTime(selectedConcernedLog.receivedAt)
                  : '-'}
              </Text>
              <Text type="secondary">
                {t('detail.logs.createdAt', 'Stored')}:{' '}
                {formatDateTime(selectedConcernedLog.createdAt)}
              </Text>
            </Space>
            {renderConcernedLogPayload(selectedConcernedLog.rawLog, 480)}
          </Space>
        ) : null}
      </Modal>
    </div>
  );
}
